/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { Table, Token, TableProps } from 'components';
import { useTranslation } from 'translation';
import { Asset } from 'types';
import {
  formatTokensToReadableValue,
  formatCentsToReadableValue,
  formatToReadablePercentage,
} from 'utilities/common';
import { useStyles } from './styles';
import { useStyles as useSharedStyles } from '../styles';
import {FaArrowDown, FaArrowUp} from 'react-icons/fa';

export interface IBorrowMarketTableProps extends Pick<TableProps, 'rowOnClick'> {
  assets: Asset[];
  isBrnEnabled: boolean;
}

const BorrowMarketTable: React.FC<IBorrowMarketTableProps> = ({
  assets,
  isBrnEnabled,
  rowOnClick,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const sharedStyles = useSharedStyles();

  const columns = useMemo(
    () => [
      { key: 'asset', label: t('markets.columns.asset'), orderable: false, align: 'left' },
      { key: 'apy', label: t('markets.columns.apy'), orderable: true, align: 'right' },
      { key: 'totalApy', label: t('markets.columns.totalApy'), orderable: true, align: 'right' },
      { key: 'wallet', label: t('markets.columns.wallet'), orderable: true, align: 'right' },
      { key: 'liquidity', label: t('markets.columns.liquidity'), orderable: true, align: 'right' },
    ],
    [],
  );

  // Format assets to rows
  const rows: TableProps['data'] = assets.map(asset => {
    const borrowApy = asset.borrowApy;
    const borrowApySign = (borrowApy.toNumber()>  0) ? true : false;  //using true for positive and false for negative
    const borrowApyArrow = borrowApySign ? <FaArrowUp/>:<FaArrowDown/>;
    const totalBorrowApy =  asset.brnBorrowApy.plus(asset.borrowApy);
    const totalBorrowApySign = (totalBorrowApy.toNumber()>  0) ? true : false;  //using true for positive and false for negative
    const totalBorrowArrow = totalBorrowApySign ? <FaArrowUp/>:<FaArrowDown/>;

    return [
      {
        key: 'asset',
        render: () => <Token tokenId={asset.id} />,
        value: asset.id,
        align: 'left',
      },
      {
        key: 'apy',
        render: () => <p css={styles.coloredValues({supplyApySign: borrowApySign})}>{borrowApyArrow}&nbsp;{formatToReadablePercentage(borrowApy.toNumber())}</p>,
        value: borrowApy.toNumber(),
        align: 'right',
      },
      {
        key: 'totalApy',
        render: () => <p css={styles.coloredValues2({supplyApySign: totalBorrowApySign})}>{totalBorrowArrow}&nbsp;{formatToReadablePercentage(totalBorrowApy.toNumber())}</p>,
        value: totalBorrowApy.toNumber(),
        align: 'right',
      },
      {
        key: 'wallet',
        render: () =>
          formatTokensToReadableValue({
            value: asset.walletBalance,
            tokenId: asset.id,
            minimizeDecimals: true,
          }),
        value: asset.walletBalance.toFixed(),
        align: 'right',
      },
      {
        key: 'liquidity',
        render: () =>
          formatCentsToReadableValue({
            value: asset.liquidity.multipliedBy(100),
            shortenLargeValue: true,
          }),
        value: asset.liquidity.toNumber(),
        align: 'right',
      },
    ];
  });

  return (
    <Table
      title={t('markets.borrowMarketTableTitle')}
      columns={columns}
      data={rows}
      initialOrder={{
        orderBy: 'apy',
        orderDirection: 'desc',
      }}
      rowKeyIndex={0}
      rowOnClick={rowOnClick}
      tableCss={sharedStyles.table}
      cardsCss={sharedStyles.cards}
      css={[sharedStyles.marketTable, sharedStyles.generalMarketTable, styles.cardContentGrid]}
    />
  );
};

export default BorrowMarketTable;
